/**
 * NotificationManagerDialog
 * ─────────────────────────
 * Extracted from App.js (Feb 2026).  The "Notification Center" full
 * dialog with filtering / sorting / bulk-actions / per-notification
 * deep-links.
 *
 * Subscribes to NotificationsContext internally so opening / paging
 * this dialog never forces the parent Dashboard to re-render.  All
 * cross-app navigation (set selected client, switch tab, etc.) is
 * still passed in as props.
 */

import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Bell,
  Calendar,
  CheckSquare,
  FileText,
  Shield,
  Folder,
  DollarSign,
  Archive,
  Trash2,
  Check,
  X,
} from 'lucide-react';

import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import { DebouncedInput } from '../ui/debounced-textarea';
import { openChartOverlay } from './ChartOverlayHost';
import { ChangeRequestReviewDialog } from './ChangeRequestReviewDialog';

import {
  useNotificationsState,
  useNotificationsActions,
} from '../../context/NotificationsContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getNotifIcon = (type) => {
  if (type?.includes('appointment')) return <Calendar className="h-4 w-4 text-blue-500" />;
  if (type?.includes('task')) return <CheckSquare className="h-4 w-4 text-green-500" />;
  if (type?.includes('treatment_plan')) return <FileText className="h-4 w-4 text-purple-500" />;
  if (type?.includes('password')) return <Shield className="h-4 w-4 text-orange-500" />;
  if (type?.includes('medical_records')) return <Folder className="h-4 w-4 text-teal-500" />;
  if (type?.includes('billing') || type?.includes('discharge')) {
    return <DollarSign className="h-4 w-4 text-green-600" />;
  }
  if (type?.includes('archive')) return <Archive className="h-4 w-4 text-gray-600" />;
  return <Bell className="h-4 w-4 text-gray-500" />;
};

const matchesFilters = (n, { search, typeFilter, statusFilter }) => {
  let match = true;
  if (search) {
    const s = search.toLowerCase();
    match = match && (
      n.title?.toLowerCase().includes(s) ||
      n.message?.toLowerCase().includes(s)
    );
  }
  if (typeFilter !== 'all') {
    match = match && (
      n.type?.includes(typeFilter) ||
      n.notification_type?.includes(typeFilter)
    );
  }
  if (statusFilter === 'unread') match = match && !n.read;
  if (statusFilter === 'read') match = match && n.read;
  return match;
};

const NotificationManagerDialogComponent = ({
  open,
  setOpen,

  // Cross-app navigation setters (Dashboard-owned)
  user,
  impersonatedUserId,
  isAdmin,
  setEditingAppointment,
  setShowAppointmentForm,
  setActiveTab,
  setSelectedClient,
  setShowClientDetail,
  setClientDetailTab,
  setBillingReviewClientId,
  setShowBillingReviewDialog,
  fetchAppointments,
}) => {
  // Subscribes to notifications data — re-renders only this dialog
  // (and the bell) when polls arrive.
  const { notifications } = useNotificationsState();
  const { fetchNotifications } = useNotificationsActions();

  // Filter / sort / selection state lives inside the dialog now.
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sort, setSort] = useState('newest');
  const [selected, setSelected] = useState([]);
  // Jun 2026 — change-request notifications open the review dialog
  // right here (previously they had no action at all).
  const [reviewChangeRequestId, setReviewChangeRequestId] = useState(null);

  const filterCtx = { search, typeFilter, statusFilter };
  const visibleNotifs = notifications.filter((n) => matchesFilters(n, filterCtx));

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col"
        data-testid="notification-manager-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Notification Center
          </DialogTitle>
          <DialogDescription>
            Manage, filter, and organize all your notifications
          </DialogDescription>
        </DialogHeader>

        {/* Filters and Search */}
        <div className="flex flex-wrap items-center gap-3 py-3 border-b">
          <div className="flex-1 min-w-[200px]">
            <DebouncedInput
              type="text"
              placeholder="Search notifications..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full"
              data-testid="notification-mgr-search"
            />
          </div>

          <div className="flex items-center gap-2">
            <Label className="text-sm whitespace-nowrap">Type:</Label>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-36" data-testid="notification-mgr-type-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="appointment">Appointments</SelectItem>
                <SelectItem value="task">Tasks</SelectItem>
                <SelectItem value="treatment_plan">Treatment Plans</SelectItem>
                <SelectItem value="password">Password</SelectItem>
                <SelectItem value="medical_records">Medical Records</SelectItem>
                <SelectItem value="system">System</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2">
            <Label className="text-sm whitespace-nowrap">Status:</Label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-28" data-testid="notification-mgr-status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="unread">Unread</SelectItem>
                <SelectItem value="read">Read</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2">
            <Label className="text-sm whitespace-nowrap">Sort:</Label>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="w-32" data-testid="notification-mgr-sort">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">Newest First</SelectItem>
                <SelectItem value="oldest">Oldest First</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {(search || typeFilter !== 'all' || statusFilter !== 'all') && (
            <Button
              variant="ghost"
              size="sm"
              data-testid="notification-mgr-clear-filters"
              onClick={() => {
                setSearch('');
                setTypeFilter('all');
                setStatusFilter('all');
              }}
            >
              Clear Filters
            </Button>
          )}
        </div>

        {/* Bulk Actions */}
        <div className="flex items-center justify-between py-2 border-b bg-gray-50 px-2 rounded">
          <div className="flex items-center gap-3">
            <Checkbox
              id="select-all-notifications"
              data-testid="notification-mgr-select-all"
              checked={selected.length > 0 && selected.length === visibleNotifs.length}
              onCheckedChange={(checked) => {
                if (checked) {
                  setSelected(visibleNotifs.map((n) => n.id));
                } else {
                  setSelected([]);
                }
              }}
            />
            <label htmlFor="select-all-notifications" className="text-sm text-gray-600 cursor-pointer">
              {selected.length > 0 ? `${selected.length} selected` : 'Select All'}
            </label>
          </div>

          {selected.length > 0 && (
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                data-testid="notification-mgr-mark-read"
                onClick={async () => {
                  try {
                    await Promise.all(selected.map((id) =>
                      axios.post(`${API}/notifications/${id}/read`)
                    ));
                    fetchNotifications();
                    setSelected([]);
                    toast.success(`Marked ${selected.length} notifications as read`);
                  } catch (error) {
                    toast.error('Failed to mark notifications as read');
                  }
                }}
              >
                <Check className="h-3 w-3 mr-1" />
                Mark as Read
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="text-red-600 border-red-300 hover:bg-red-50"
                data-testid="notification-mgr-dismiss-selected"
                onClick={async () => {
                  const results = await Promise.allSettled(selected.map((id) =>
                    axios.post(`${API}/notifications/${id}/dismiss`)
                  ));
                  const failed = results.filter((r) => r.status === 'rejected').length;
                  fetchNotifications();
                  setSelected([]);
                  if (failed === 0) {
                    toast.success(`Dismissed ${selected.length} notifications`);
                  } else if (failed < selected.length) {
                    toast.warning(`Dismissed ${selected.length - failed} of ${selected.length} notifications`);
                  } else {
                    toast.error('Failed to dismiss notifications');
                  }
                }}
              >
                <Trash2 className="h-3 w-3 mr-1" />
                Dismiss Selected
              </Button>
            </div>
          )}

          {selected.length === 0 && notifications.length > 0 && (
            <Button
              size="sm"
              variant="ghost"
              className="text-red-600"
              data-testid="notification-mgr-dismiss-all"
              onClick={async () => {
                if (window.confirm('Are you sure you want to dismiss ALL notifications?')) {
                  try {
                    await axios.post(`${API}/notifications/dismiss-all`);
                    fetchNotifications();
                    toast.success('All notifications dismissed');
                  } catch (error) {
                    toast.error('Failed to dismiss notifications');
                  }
                }
              }}
            >
              <Trash2 className="h-3 w-3 mr-1" />
              Dismiss All
            </Button>
          )}
        </div>

        {/* Notifications List */}
        <div className="flex-1 overflow-y-auto mt-2">
          {(() => {
            let filtered = [...visibleNotifs];

            filtered.sort((a, b) => {
              const dateA = new Date(a.created_at);
              const dateB = new Date(b.created_at);
              return sort === 'newest' ? dateB - dateA : dateA - dateB;
            });

            if (filtered.length === 0) {
              return (
                <div className="text-center py-12 text-gray-500" data-testid="notification-mgr-empty">
                  <Bell className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                  <p className="text-lg font-medium">No notifications found</p>
                  <p className="text-sm mt-1">
                    {notifications.length > 0
                      ? 'Try adjusting your filters'
                      : "You're all caught up!"}
                  </p>
                </div>
              );
            }

            // Group by date
            const grouped = filtered.reduce((acc, notif) => {
              const date = new Date(notif.created_at);
              const today = new Date();
              const yesterday = new Date(today);
              yesterday.setDate(yesterday.getDate() - 1);

              let dateKey;
              if (date.toDateString() === today.toDateString()) {
                dateKey = 'Today';
              } else if (date.toDateString() === yesterday.toDateString()) {
                dateKey = 'Yesterday';
              } else if (date > new Date(today.setDate(today.getDate() - 7))) {
                dateKey = 'This Week';
              } else {
                dateKey = 'Older';
              }

              if (!acc[dateKey]) acc[dateKey] = [];
              acc[dateKey].push(notif);
              return acc;
            }, {});

            const dateOrder = ['Today', 'Yesterday', 'This Week', 'Older'];

            return dateOrder.map((dateKey) => {
              if (!grouped[dateKey] || grouped[dateKey].length === 0) return null;

              return (
                <div key={dateKey} className="mb-4">
                  <div className="sticky top-0 bg-gray-100 px-3 py-1.5 text-sm font-semibold text-gray-600 rounded">
                    {dateKey} ({grouped[dateKey].length})
                  </div>
                  <div className="space-y-2 mt-2">
                    {grouped[dateKey].map((notif) => (
                      <div
                        key={notif.id}
                        className={`flex items-start gap-3 p-3 rounded-lg border ${
                          selected.includes(notif.id) ? 'bg-blue-50 border-blue-200' :
                          !notif.read ? 'bg-yellow-50 border-yellow-200' : 'bg-white border-gray-200'
                        } hover:shadow-sm transition-shadow`}
                        data-testid={`notification-mgr-item-${notif.id}`}
                      >
                        <Checkbox
                          checked={selected.includes(notif.id)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelected((prev) => [...prev, notif.id]);
                            } else {
                              setSelected((prev) => prev.filter((id) => id !== notif.id));
                            }
                          }}
                        />

                        <div className="flex-shrink-0 mt-0.5">
                          {getNotifIcon(notif.type || notif.notification_type)}
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <p className={`font-medium text-sm ${!notif.read ? 'text-gray-900' : 'text-gray-700'}`}>
                                {notif.title}
                                {!notif.read && (
                                  <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                    New
                                  </span>
                                )}
                              </p>
                              <p className="text-sm text-gray-600 mt-0.5">{notif.message}</p>
                              <p className="text-xs text-gray-400 mt-1">
                                {new Date(notif.created_at).toLocaleString()}
                              </p>
                            </div>

                            <div className="flex items-center gap-1 flex-shrink-0">
                              {!notif.read && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 w-7 p-0"
                                  title="Mark as read"
                                  data-testid={`notification-mgr-mark-read-${notif.id}`}
                                  onClick={async () => {
                                    try {
                                      await axios.post(`${API}/notifications/${notif.id}/read`);
                                      fetchNotifications();
                                    } catch (error) {
                                      toast.error('Failed to mark as read');
                                    }
                                  }}
                                >
                                  <Check className="h-3 w-3" />
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 w-7 p-0 text-red-500 hover:text-red-700"
                                title="Dismiss"
                                data-testid={`notification-mgr-dismiss-${notif.id}`}
                                onClick={async () => {
                                  try {
                                    await axios.post(`${API}/notifications/${notif.id}/dismiss`);
                                    fetchNotifications();
                                    setSelected((prev) => prev.filter((id) => id !== notif.id));
                                  } catch (error) {
                                    toast.error('Failed to dismiss');
                                  }
                                }}
                              >
                                <X className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>

                          {/* Action buttons based on notification type */}
                          {notif.type === 'era_received' && notif.data?.era_id && (
                            <div className="mt-2">
                              <Button
                                size="sm"
                                variant="link"
                                className="p-0 h-auto text-teal-600 font-semibold"
                                data-testid={`notif-manager-open-era-${notif.id}`}
                                onClick={async () => {
                                  try { await axios.post(`${API}/notifications/${notif.id}/read`); } catch { /* best-effort */ }
                                  setOpen(false);
                                  window.location.href = `/v2/era?eraId=${encodeURIComponent(notif.data.era_id)}`;
                                }}
                              >
                                Open ERA →
                              </Button>
                            </div>
                          )}
                          {notif.type?.includes('change_request') && (notif.change_request_id || notif.reference_id) && (
                            <div className="mt-2">
                              <Button
                                size="sm"
                                variant="link"
                                className="p-0 h-auto text-blue-600 font-semibold"
                                data-testid={`notif-manager-open-change-request-${notif.id}`}
                                onClick={async () => {
                                  try { await axios.post(`${API}/notifications/${notif.id}/read`); } catch { /* best-effort */ }
                                  setReviewChangeRequestId(notif.change_request_id || notif.reference_id);
                                }}
                              >
                                View Change Request →
                              </Button>
                            </div>
                          )}
                          {(notif.appointment_id || notif.type === 'password_reset_request' ||
                            notif.type === 'medical_records_request' || notif.type?.includes('treatment_plan') ||
                            notif.type === 'discharge_billing_review' || notif.type === 'client_ready_for_archive' ||
                            notif.type === 'appointment_invitation') && (
                            <div className="mt-2">
                              {notif.type === 'appointment_invitation' && notif.appointment_id && (
                                <div className="flex items-center gap-2">
                                  <Button
                                    size="sm"
                                    className="bg-green-600 hover:bg-green-700 text-white"
                                    onClick={async () => {
                                      try {
                                        const onBehalfOf = impersonatedUserId ? `&on_behalf_of=${impersonatedUserId}` : '';
                                        await axios.post(`${API}/appointments/${notif.appointment_id}/respond-invitation?response=accepted${onBehalfOf}`);
                                        toast.success('Invitation accepted');
                                        await axios.post(`${API}/notifications/${notif.id}/read`);
                                        fetchNotifications();
                                        fetchAppointments?.();
                                      } catch (err) {
                                        toast.error(err.response?.data?.detail || 'Failed to accept');
                                      }
                                    }}
                                  >
                                    Accept
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="border-red-300 text-red-600 hover:bg-red-50"
                                    onClick={async () => {
                                      try {
                                        const onBehalfOf = impersonatedUserId ? `&on_behalf_of=${impersonatedUserId}` : '';
                                        await axios.post(`${API}/appointments/${notif.appointment_id}/respond-invitation?response=declined${onBehalfOf}`);
                                        toast.success('Invitation declined');
                                        await axios.post(`${API}/notifications/${notif.id}/read`);
                                        fetchNotifications();
                                        fetchAppointments?.();
                                      } catch (err) {
                                        toast.error(err.response?.data?.detail || 'Failed to decline');
                                      }
                                    }}
                                  >
                                    Decline
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="link"
                                    className="p-0 h-auto text-blue-600"
                                    onClick={async () => {
                                      try {
                                        // Feb 2026 perf fix: lookup single appointment by id
                                        const response = await axios.get(`${API}/appointments/${notif.appointment_id}`);
                                        const appointment = response.data;
                                        if (appointment) {
                                          setEditingAppointment(appointment);
                                          setShowAppointmentForm(true);
                                          setActiveTab('appointments');
                                          setOpen(false);
                                        }
                                      } catch (err) {
                                        toast.error('Failed to load appointment');
                                      }
                                    }}
                                  >
                                    View →
                                  </Button>
                                </div>
                              )}
                              {notif.appointment_id && notif.type !== 'appointment_invitation' && (
                                <Button
                                  size="sm"
                                  variant="link"
                                  className="p-0 h-auto text-blue-600"
                                  onClick={async () => {
                                    try {
                                      // Feb 2026 perf fix: lookup single appointment by id
                                      const response = await axios.get(`${API}/appointments/${notif.appointment_id}`);
                                      const appointment = response.data;
                                      if (appointment) {
                                        setEditingAppointment(appointment);
                                        setShowAppointmentForm(true);
                                        setActiveTab('appointments');
                                        setOpen(false);
                                        await axios.post(`${API}/notifications/${notif.id}/read`);
                                        fetchNotifications();
                                      } else {
                                        toast.error('Appointment not found');
                                      }
                                    } catch (err) {
                                      toast.error('Failed to load appointment');
                                    }
                                  }}
                                >
                                  View Appointment →
                                </Button>
                              )}
                              {notif.type === 'password_reset_request' && (
                                <Button
                                  size="sm"
                                  variant="link"
                                  className="p-0 h-auto text-blue-600"
                                  onClick={async () => {
                                    setActiveTab('admin');
                                    setOpen(false);
                                    await axios.post(`${API}/notifications/${notif.id}/read`);
                                    fetchNotifications();
                                  }}
                                >
                                  View Password Reset Request →
                                </Button>
                              )}
                              {notif.type === 'medical_records_request' && (
                                <Button
                                  size="sm"
                                  variant="link"
                                  className="p-0 h-auto text-blue-600"
                                  onClick={async () => {
                                    setActiveTab('medical-records');
                                    setOpen(false);
                                    await axios.post(`${API}/notifications/${notif.id}/read`);
                                    fetchNotifications();
                                  }}
                                >
                                  View Medical Records Request →
                                </Button>
                              )}
                              {notif.type?.includes('treatment_plan') && notif.data?.client_id && (
                                <Button
                                  size="sm"
                                  variant="link"
                                  className="p-0 h-auto text-purple-600"
                                  onClick={async () => {
                                    try {
                                      const clientResponse = await axios.get(`${API}/clients/${notif.data.client_id}`);
                                      setSelectedClient(clientResponse.data);
                                      setClientDetailTab('treatment-plans');
                                      setShowClientDetail(true);
                                      setOpen(false);
                                      await axios.post(`${API}/notifications/${notif.id}/read`);
                                      fetchNotifications();
                                    } catch (err) {
                                      toast.error('Failed to load treatment plan');
                                    }
                                  }}
                                >
                                  View Treatment Plan →
                                </Button>
                              )}
                              {notif.type === 'statement_payment_received' && (notif.data?.client_id || notif.client_id) && (
                                <Button
                                  size="sm"
                                  variant="link"
                                  className="p-0 h-auto text-emerald-600 font-semibold"
                                  data-testid={`notif-open-billing-chart-${notif.id}`}
                                  onClick={async () => {
                                    try { await axios.post(`${API}/notifications/${notif.id}/read`); } catch { /* best-effort */ }
                                    setOpen(false);
                                    openChartOverlay(notif.data?.client_id || notif.client_id, 'billing');
                                  }}
                                >
                                  Open Billing Chart to Apply Payment →
                                </Button>
                              )}
                              {notif.type === 'discharge_billing_review' && (
                                <div className="flex items-center gap-3">
                                  <Button
                                    size="sm"
                                    variant="link"
                                    className="p-0 h-auto text-orange-600 font-semibold"
                                    data-testid={`notif-open-review-task-${notif.id}`}
                                    onClick={async () => {
                                      try { await axios.post(`${API}/notifications/${notif.id}/read`); } catch { /* best-effort */ }
                                      setOpen(false);
                                      const tid = notif.task_id ? `?taskId=${encodeURIComponent(notif.task_id)}` : '';
                                      window.location.href = `/v2/tasks${tid}`;
                                    }}
                                  >
                                    Open Review Task →
                                  </Button>
                                  {notif.client_id && (
                                    <Button
                                      size="sm"
                                      variant="link"
                                      className="p-0 h-auto text-blue-600"
                                      data-testid={`notif-open-chart-${notif.id}`}
                                      onClick={async () => {
                                        try { await axios.post(`${API}/notifications/${notif.id}/read`); } catch { /* best-effort */ }
                                        setOpen(false);
                                        openChartOverlay(notif.client_id);
                                      }}
                                    >
                                      Open Chart →
                                    </Button>
                                  )}
                                </div>
                              )}
                              {notif.type === 'client_ready_for_archive' && notif.client_id && (
                                <Button
                                  size="sm"
                                  variant="link"
                                  className="p-0 h-auto text-green-600"
                                  onClick={async () => {
                                    try {
                                      const clientResponse = await axios.get(`${API}/clients/${notif.client_id}`);
                                      setSelectedClient(clientResponse.data);
                                      setShowClientDetail(true);
                                      setOpen(false);
                                      await axios.post(`${API}/notifications/${notif.id}/read`);
                                      fetchNotifications();
                                    } catch (err) {
                                      toast.error('Failed to load client');
                                    }
                                  }}
                                >
                                  View Client to Archive →
                                </Button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            });
          })()}
        </div>

        {/* Footer Stats */}
        <div className="border-t pt-3 mt-2 flex items-center justify-between text-sm text-gray-500">
          <span data-testid="notification-mgr-total">
            Total: {notifications.length} notifications ({notifications.filter((n) => !n.read).length} unread)
          </span>
          <Button
            variant="outline"
            size="sm"
            data-testid="notification-mgr-close"
            onClick={() => setOpen(false)}
          >
            Close
          </Button>
        </div>
      </DialogContent>
      <ChangeRequestReviewDialog
        requestId={reviewChangeRequestId}
        open={!!reviewChangeRequestId}
        onOpenChange={(o) => { if (!o) setReviewChangeRequestId(null); }}
        user={user}
        onActioned={fetchNotifications}
      />
    </Dialog>
  );
};

/**
 * React.memo: the parent (Dashboard) does not subscribe to
 * NotificationsState anymore, so the only way this dialog re-renders
 * via context is by directly subscribing to useNotificationsState
 * inside the component above.  Memo gives us the bonus that unrelated
 * Dashboard prop changes (since most setters are stable) won't bust
 * equality.
 */
export const NotificationManagerDialog = React.memo(NotificationManagerDialogComponent);

export default NotificationManagerDialog;
